import React, { useState, useEffect } from 'react';
import { educationData, EducationCategory } from '../data/educationContent';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';

export const Education: React.FC = () => {
  const { t } = useT();
  const [selectedCategory, setSelectedCategory] = useState<string>(educationData[0]?.id || '');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const saved = localStorage.getItem('education_progress');
    if (saved) setProgress(JSON.parse(saved));
  }, []);

  const toggleItem = (itemKey: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  };

  const markAsRead = (itemKey: string) => {
    const updated = { ...progress, [itemKey]: true };
    setProgress(updated);
    localStorage.setItem('education_progress', JSON.stringify(updated));
  };

  const currentCategory: EducationCategory | undefined = educationData.find(c => c.id === selectedCategory);
  const totalItems = educationData.reduce((sum, cat) => sum + cat.items.length, 0);
  const completedItems = Object.values(progress).filter(Boolean).length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">
          {t('education.title')}
        </h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">
          {t('education.subtitle')}
        </p>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between mb-1.5">
            <span className="text-[13px] text-text-muted">{t('education.progress')}</span>
            <span className="text-[13px] text-accent font-bold">
              {completedItems}/{totalItems} ({progressPercent}%)
            </span>
          </div>
          <div className="h-1.5 bg-border rounded-full">
            <div
              className="h-full bg-gradient-to-r from-accent to-purple-600 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-6 lg:gap-8">
        {/* Left: Categories */}
        <div>
          <h3 className="text-sm text-text-muted mb-3 font-bold">
            {t('education.categories')}
          </h3>
          <div className="flex flex-col gap-2">
            {educationData.map(category => {
              const catItems = category.items.length;
              const catCompleted = category.items.filter(
                (_, i) => progress[`${category.id}-${i}`]
              ).length;
              const isActive = selectedCategory === category.id;

              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    'p-3.5 rounded-xl cursor-pointer text-left transition-all',
                    isActive
                      ? 'bg-accent/10 border-2 border-accent text-accent'
                      : 'bg-surface-2 border border-border text-text-primary hover:border-accent/60'
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{category.icon}</span>
                    <div>
                      <div className="text-[15px] font-bold">{category.title}</div>
                      <div className="text-xs text-text-muted mt-0.5">
                        {t('education.completed', { done: catCompleted, total: catItems })}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Content */}
        <div>
          {currentCategory && (
            <>
              <h2 className="text-xl lg:text-2xl mb-5 text-text-primary">
                {currentCategory.icon} {currentCategory.title}
              </h2>

              <div className="flex flex-col gap-3">
                {currentCategory.items.map((item, index) => {
                  const itemKey = `${currentCategory.id}-${index}`;
                  const isExpanded = expandedItems.has(itemKey);
                  const isCompleted = progress[itemKey];

                  return (
                    <div
                      key={itemKey}
                      className={cn(
                        'bg-surface-2 rounded-xl overflow-hidden border',
                        isCompleted ? 'border-success' : 'border-border'
                      )}
                    >
                      {/* Accordion Header */}
                      <button
                        onClick={() => toggleItem(itemKey)}
                        className="w-full py-[18px] px-5 bg-transparent border-none text-text-primary cursor-pointer text-left flex justify-between items-center"
                      >
                        <div className="flex items-center gap-3">
                          {isCompleted && (
                            <span className="text-success text-base">✓</span>
                          )}
                          <span className="text-base font-bold">{item.title}</span>
                        </div>
                        <span className={cn(
                          'text-sm text-text-muted transition-transform duration-200',
                          isExpanded && 'rotate-180'
                        )}>
                          ▼
                        </span>
                      </button>

                      {/* Accordion Content */}
                      {isExpanded && (
                        <div className="px-5 pb-5">
                          <div className="text-[15px] leading-[1.8] text-text-primary whitespace-pre-wrap pt-2.5 border-t border-border">
                            {item.content}
                          </div>

                          {item.codeExample && (
                            <div className="mt-4">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-text-muted">{t('education.codeExample')}</span>
                                <button
                                  onClick={() => navigator.clipboard.writeText(item.codeExample!)}
                                  className="py-1 px-2.5 bg-accent border-none rounded-md text-white cursor-pointer text-[11px]"
                                >
                                  {t('common.copy')}
                                </button>
                              </div>
                              <pre className="p-4 bg-bg rounded-lg text-text-primary text-[13px] overflow-auto m-0">
                                {item.codeExample}
                              </pre>
                            </div>
                          )}

                          {!isCompleted && (
                            <button
                              onClick={() => markAsRead(itemKey)}
                              className="mt-4 py-2.5 px-5 bg-success/10 border border-success rounded-full text-success cursor-pointer text-sm font-bold hover:bg-success/20 transition-colors"
                            >
                              {t('education.markAsRead')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
